import React, { useEffect, useRef, useState } from "react";
import { motion, useAnimation } from "framer-motion";

import serverImage from "../../../../assets/servers.png";

const variants = {
  initial: { opacity: 0, scale: 0.95 },
  animate: { opacity: 1, scale: 1 },
  exit: { opacity: 0, scale: 0.95 },
  transition: { duration: 0.5, ease: "easeInOut" },
};

const videoVariants = {
  initial: { y: -20, opacity: 0 },
  animate: (i) => ({
    y: 0,
    opacity: 1,
    scale: 1,
    transition: { type: "spring", stiffness: 200, damping: 20, delay: i * 0.2 },
  }),
};

const flickerVariants = {
  rest: { filter: "brightness(1)" },
  flicker: {
    filter: "brightness(1.5)",
    transition: { yoyo: 2, duration: 0.2 },
  },
};

const containerVariants = {
  initial: { justifyContent: "center", opacity: 0.8, scale: 0.9 },
  animate: {
    justifyContent: "space-between",
    opacity: 1,
    scale: 1,
    transition: {
      duration: 0.8,
      ease: "easeOut",
      when: "beforeChildren",
      staggerChildren: 0.2,
    },
  },
};

const childVariants = {
  initial: { opacity: 0 },
  animate: { opacity: 1, transition: { duration: 0.6 } },
};

const messageColors = ["red", "yellow", "green", "blue"];

const Slide1Ingestion = () => {
  const controls = useAnimation();
  const ref = useRef(null);
  useEffect(() => {
    if (!ref.current) return;
    const obs = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) controls.start("animate");
        else controls.start("initial");
      },
      { threshold: 0.3 }
    );
    obs.observe(ref.current);
    return () => obs.disconnect();
  }, [controls]);

  const [copies, setCopies] = useState([1]);
  useEffect(() => {
    const timer = setTimeout(() => setCopies([0, 1, 2]), 300);
    return () => clearTimeout(timer);
  }, []);

  const [messages, setMessages] = useState([]);
  const [flickers, setFlickers] = useState(
    Array(messageColors.length).fill(false)
  );

  useEffect(() => {
    const interval = setInterval(() => {
      const color =
        messageColors[Math.floor(Math.random() * messageColors.length)];
      const server = Math.floor(Math.random() * copies.length);
      const id = Date.now();
      setMessages((msgs) => [...msgs, { id, color, server }]);
      setTimeout(() => {
        setMessages((msgs) => msgs.filter((m) => m.id !== id));
        const idx = messageColors.indexOf(color);
        setFlickers((fs) => {
          const next = [...fs];
          next[idx] = true;
          return next;
        });
        setTimeout(() => {
          setFlickers((fs) => {
            const next = [...fs];
            next[idx] = false;
            return next;
          });
        }, 200);
      }, 150);
    }, 700);
    return () => clearInterval(interval);
  }, [copies]);

  return (
    <motion.section
      variants={variants}
      initial="initial"
      animate={controls}
      ref={ref}
      exit="exit"
      className="h-full min-h-screen flex items-center justify-center bg-gray-50"
    >
      <motion.div
        className="w-3/4 max-w-6xl mx-auto flex flex-col md:flex-row items-center md:gap-10 py-8"
        variants={containerVariants}
        initial="initial"
        animate={controls}
      >
        <motion.div
          className="lg:w-1/2 mb-6 lg:mb-0"
          variants={childVariants}
          initial="initial"
          animate={controls}
        >
          <h2 className="text-2xl font-bold">
            Easily collect categorized events
          </h2>
          <p className="text-gray-700 my-4 text-justify">
            Loghook recieves events from multiple sources in real time and
            provides a flexible, customizable dashboard builder—so you can
            visualize, group, and act on your data without writing a single line
            of code.
          </p>

          <p className="text-gray-700 text-justify">
            Everything through one simple web request.
          </p>
        </motion.div>
        <motion.div
          className="lg:w-1/2 relative"
          variants={childVariants}
          initial="initial"
          animate={controls}
        >
          <div className="p-3 bg-gray-100 rounded-2xl">
            {/* Server video spawning */}
            <div className="flex justify-around items-center h-32 mb-8">
              {copies.map((_, i) => (
                <div key={i} className="relative flex flex-col items-center">
                  <motion.img
                    src={serverImage}
                    className="w-20 h-20 object-cover "
                    variants={videoVariants}
                    custom={i}
                    animate={controls}
                  />
                  {messages
                    .filter((m) => m.server === i)
                    .map(({ id, color }) => (
                      <motion.div
                        key={id}
                        className={`absolute w-3 h-3 rounded-full bg-${color}-500`}
                        style={{ top: "100%", left: "50%", translateX: "-50%" }}
                        initial={{ y: 0, opacity: 1 }}
                        animate={{ y: 80, opacity: [1, 1, 0] }}
                        transition={{ duration: 0.5 }}
                      />
                    ))}
                </div>
              ))}
            </div>
            {/* Dashboard + Dots + Topics */}
            <div className="flex justify-center w-full">
              <div className="relative">
                <div className="w-80 h-60 bg-white rounded-lg shadow relative overflow-hidden">
                  {/* Packed dashboard tiles */}
                  <div className="absolute inset-2 grid grid-cols-3 grid-rows-3 gap-1">
                    <motion.div
                      variants={flickerVariants}
                      initial="rest"
                      animate={flickers[0] ? "flicker" : "rest"}
                      className="col-span-2 row-span-1 bg-red-200 rounded"
                    />
                    <motion.div
                      variants={flickerVariants}
                      initial="rest"
                      animate={flickers[1] ? "flicker" : "rest"}
                      className="col-span-1 row-span-2 bg-yellow-200 rounded"
                    />
                    <motion.div
                      variants={flickerVariants}
                      initial="rest"
                      animate={flickers[2] ? "flicker" : "rest"}
                      className="col-span-1 row-span-1 bg-green-200 rounded"
                    />
                    <motion.div
                      variants={flickerVariants}
                      initial="rest"
                      animate={flickers[3] ? "flicker" : "rest"}
                      className="col-span-3 row-span-1 bg-blue-200 rounded"
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </motion.div>
      </motion.div>
    </motion.section>
  );
};

export default Slide1Ingestion;
